import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { MobileInvoiceModal } from "./mobileInvoicesModal";
import { MobileAddClient } from "../landing/mobileAddClient";
import "./mobileDetails.css";
import api from "../../../api";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatINR = (value) => {
  if (value === null || value === undefined) return "-";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

const INTERACTION_MODE_OPTIONS = [
  { label: "In Person",         value: "In Person",         icon: "person"       },
  { label: "Audio Call",        value: "Audio Call",        icon: "call"         },
  { label: "Video Call",        value: "Video Call",        icon: "videocam"     },
  { label: "Email",             value: "Email",             icon: "email"        },
  { label: "Could Not Connect", value: "Could Not Connect", icon: "phone_missed" },
];

const commTypeIcon = (type) => {
  if (!type) return "chat";
  const t = type.toLowerCase();
  if (t.includes("could not") || t.includes("missed")) return "block";
  if (t.includes("audio")) return "call";
  if (t.includes("video")) return "videocam";
  if (t.includes("email")) return "email";
  if (t.includes("person") || t.includes("visit")) return "person";
  return "chat";
};

export const MobileDetails = () => {
  const { partyId } = useParams();
  const location    = useLocation();
  const navigate    = useNavigate();
  const salesPersonId = localStorage.getItem("salesPersonId");

  const partyName  = location.state?.partyName  || "Client Details";
  const partyPhone = location.state?.partyPhone || null;
  const partyEmail = location.state?.partyEmail || null;

  const [apiResponse,      setApiResponse]      = useState(null);
  const [reconnectDate,    setReconnectDate]    = useState("");
  const [interactionMode,  setInteractionMode]  = useState("In Person");
  const [interactionNote,  setInteractionNote]  = useState("");
  const [submitting,       setSubmitting]       = useState(false);
  const [submitMsg,        setSubmitMsg]        = useState("");
  const [communications,   setCommunications]   = useState([]);
  const [commsLoading,     setCommsLoading]     = useState(true);
  const [showInvoices,     setShowInvoices]     = useState(false);
  const [showUpdateClient, setShowUpdateClient] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await api.get("/api/innerpage", {
          params: { SalespersonId: salesPersonId, PartyId: partyId },
        });
        setApiResponse(response.data);
        const reconnect = response.data?.data?.reconnectInfo?.ReconnectDate;
        if (reconnect) {
          setReconnectDate(new Date(reconnect).toISOString().split("T")[0]);
        }
      } catch (error) {
        console.error("Inner Page API Error:", error.response?.data || error.message);
      }
    };
    fetchDetails();
  }, [partyId]);

  const fetchComms = async () => {
    try {
      setCommsLoading(true);
      const response = await api.get("/api/communication/list", {
        params: { PartyId: partyId },
      });
      const list = response.data?.data || response.data || [];
      setCommunications(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Communication List Error:", error.response?.data || error.message);
      setCommunications([]);
    } finally {
      setCommsLoading(false);
    }
  };

  useEffect(() => {
    fetchComms();
  }, [partyId]);

  const handleSubmitInteraction = async () => {
    if (!interactionNote.trim()) {
      setSubmitMsg("Please add interaction notes before submitting.");
      setTimeout(() => setSubmitMsg(""), 3000);
      return;
    }
    if (!reconnectDate) {
      setSubmitMsg("Please select a reconnect date.");
      setTimeout(() => setSubmitMsg(""), 3000);
      return;
    }
    setSubmitting(true);
    setSubmitMsg("");
    try {
      await api.post("/api/communication/addupdate", {
        CommType:      interactionMode,
        EmployeeId:    salesPersonId,
        PartyId:       Number(partyId),
        Note:          interactionNote,
        ReconnectDate: new Date(reconnectDate).toISOString(),
      });
      setSubmitMsg("Interaction saved successfully!");
      setInteractionNote("");
      await fetchComms();
    } catch (error) {
      console.error("Submit Interaction Error:", error.response?.data || error.message);
      setSubmitMsg("Failed to save interaction. Please try again.");
    } finally {
      setSubmitting(false);
      setTimeout(() => setSubmitMsg(""), 4000);
    }
  };

  const handleExportCSV = () => {
    const monthlySummary = apiResponse?.data?.monthlySummary || [];
    const headers = ["Month", "Quantity", "Sales Value", "Receipts", "Order Share"];
    const rows    = monthlySummary.map((row) => [
      `${MONTH_NAMES[row.Mn]} ${row.Yr}`,
      row.TotalQty, row.InvoiceAmt, row.ReceiptAmt, `${row["Order Share"]}%`,
    ]);
    const csv  = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `${partyName}_sales_history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!apiResponse) return <div className="mobileDetailsLoading">Loading...</div>;

  const details        = apiResponse.data;
  const sixMonthSales  = details.sixMonthSales?.[0]  || {};
  const creditSummary  = details.creditSummary        || {};
  const monthlySummary = details.monthlySummary       || [];
  const invoiceList    = details.invoiceOutstanding   || [];

  const monthlyPotential = sixMonthSales["Potential Sale"]    || 0;
  const top6MonthSales   = sixMonthSales["Top 6 Month Sales"] || 0;
  const outstandingAmt   = creditSummary["Out Standing"]      || 0;
  const overdueAmt       = creditSummary["Over Due"]          || 0;
  const creditDays       = creditSummary["Cr. Days"]          || 0;

  return (
    <div className="mobileDetailsMain">

      {/* ── Header ── */}
      <div className="mobileDetailsHeader">
        <span
          className="material-symbols-outlined mobileDetailsBackIcon"
          onClick={() => navigate(-1)}
        >
          arrow_back
        </span>
        <p className="mobileDetailsHeaderTitle">{partyName}</p>
      </div>

      {/* ── Scrollable body ── */}
      <div className="mobileDetailsBody">

        {/* Client Name Card */}
        <div className="mobileDetailsClientCard">
          <div className="mobileDetailsClientTop">
            <div className="mobileDetailsClientIconBox">
              <span className="material-symbols-outlined">store</span>
            </div>
            <div className="mobileDetailsClientInfo">
              <p className="mobileDetailsClientName">{partyName}</p>
              <span className="mobileDetailsActiveBadge">Active</span>
            </div>
          </div>
          <button
            className="mobileDetailsUpdateBtn"
            onClick={() => setShowUpdateClient(true)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: "3.5vw" }}>edit</span>
            Update Client
          </button>
          <div className="mobileDetailsActionBtns">
            <button
              className="mobileDetailsCallBtn"
              disabled={!partyPhone}
              onClick={() => partyPhone && window.open(`tel:${partyPhone}`)}
            >
              <span className="material-symbols-outlined" style={{ fontSize: "4.5vw" }}>call</span>
              Call
            </button>
            <button
              className="mobileDetailsEmailBtn"
              disabled={!partyEmail}
              onClick={() => partyEmail && window.open(`mailto:${partyEmail}`)}
            >
              <span className="material-symbols-outlined" style={{ fontSize: "4.5vw" }}>mail</span>
              Email
            </button>
          </div>
        </div>

        {/* Interaction Card */}
        <div className="mobileDetailsInteractionCard">
          {/* Reconnect Date */}
          <div className="mobileDetailsFieldGroup">
            <p className="mobileDetailsFieldLabel">Reconnect Date</p>
            <div className="mobileDetailsDateWrapper">
              <span className="mobileDetailsDateText">
                {reconnectDate
                  ? new Date(reconnectDate + "T00:00:00").toLocaleDateString("en-IN")
                  : "Select date..."}
              </span>
              <span className="material-symbols-outlined mobileDetailsCalIcon">calendar_today</span>
              <input
                type="date"
                className="mobileDetailsNativeDate"
                value={reconnectDate}
                onChange={(e) => setReconnectDate(e.target.value)}
              />
            </div>
          </div>

          {/* Interaction Mode */}
          <div className="mobileDetailsVisitedRow">
            <p className="mobileDetailsVisitedLabel">Interaction Mode</p>
            <div className="mobileDetailsSelectWrapper">
              <select
                className="mobileDetailsSelect"
                value={interactionMode}
                onChange={(e) => setInteractionMode(e.target.value)}
              >
                {INTERACTION_MODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="material-symbols-outlined mobileDetailsSelectArrow">expand_more</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="mobileDetailsNotesLabel">Interaction Notes</p>
            <textarea
              className="mobileDetailsNotesTextarea"
              placeholder="Add detailed notes about your call, action items, or next steps here..."
              value={interactionNote}
              onChange={(e) => setInteractionNote(e.target.value)}
            />
          </div>

          {submitMsg && (
            <p className={`mobileDetailsSubmitMsg${submitMsg.includes("success") ? " mobileDetailsSubmitSuccess" : " mobileDetailsSubmitError"}`}>
              {submitMsg}
            </p>
          )}

          <button
            className="mobileDetailsSubmitBtn"
            onClick={handleSubmitInteraction}
            disabled={submitting}
          >
            <span className="material-symbols-outlined">{submitting ? "hourglass_top" : "send"}</span>
            {submitting ? "Submitting..." : "Submit Interaction"}
          </button>
        </div>

        {/* Recent Interactions */}
        <div className="mobileDetailsRecentCard">
          <p className="mobileDetailsRecentTitle">Recent Interactions</p>
          {commsLoading ? (
            <p className="mobileDetailsCommsEmpty">Loading interactions...</p>
          ) : communications.length === 0 ? (
            <p className="mobileDetailsCommsEmpty">No interactions recorded yet.</p>
          ) : (
            communications.map((item, idx) => (
              <div className="mobileDetailsInteractionItem" key={idx}>
                <div className="mobileDetailsInteractionIcon">
                  <span className="material-symbols-outlined">{commTypeIcon(item.TrnCommType)}</span>
                </div>
                <div className="mobileDetailsInteractionContent">
                  <div className="mobileDetailsInteractionMeta">
                    <span className="mobileDetailsInteractionDate">
                      {item.TrnCommDate
                        ? new Date(item.TrnCommDate).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })
                        
                        : "-"}
                    </span>
                    <span className="mobileDetailsInteractionSep">|</span>
                    <span className="mobileDetailsInteractionType">{item.TrnCommType || "Note"}</span>
                  </div>
                  <p className="mobileDetailsInteractionNote">{item.TrnCommNote}</p>
                  {item.TrnCommReconnectDate && (
                    <p style={{ color: "#818cf8", fontSize: "3vw", margin: "1vw 0 0 0" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "3vw", verticalAlign: "middle" }}>event</span>
                      {" "}Reconnect: {new Date(item.TrnCommReconnectDate).toLocaleDateString("en-IN")}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Monthly Potential */}
        <div className="mobileDetailsPotentialCard">
          <div className="mobileDetailsPotentialHeader">
            <p className="mobileDetailsPotentialLabel">Monthly Potential</p>
            <div className="mobileDetailsPotentialIcon">
              <span className="material-symbols-outlined">pie_chart</span>
            </div>
          </div>
          <p className="mobileDetailsPotentialAmount">{formatINR(monthlyPotential)}</p>
          <div className="mobileDetailsPotentialFooter">
            <span className="mobileDetailsPotentialFooterLabel">Top 6 Month Sales</span>
            <span className="mobileDetailsPotentialFooterValue">{formatINR(top6MonthSales)}</span>
          </div>
        </div>

        {/* Outstanding Balance */}
        <div className="mobileDetailsOutstandingCard">
          <div className="mobileDetailsOutstandingHeader">
            <p className="mobileDetailsOutstandingTitle">Outstanding Balance</p>
            <button
              className="mobileDetailsViewInvoicesBtn"
              onClick={() => setShowInvoices(true)}
            >
              View Invoices
              <span className="material-symbols-outlined" style={{ fontSize: "3.5vw" }}>open_in_new</span>
            </button>
          </div>
          <p className="mobileDetailsOutstandingAmt">{formatINR(overdueAmt)}</p>
          <p className="mobileDetailsOverdueLabel">Current Overdue Amount</p>
          <hr className="mobileDetailsDivider" />
          <div className="mobileDetailsOutstandingRow">
            <span className="mobileDetailsOutstandingRowLabel">Credit Days</span>
            <span className="mobileDetailsOutstandingRowValue">{creditDays}</span>
          </div>
          <hr className="mobileDetailsDivider" />
          <div className="mobileDetailsOutstandingRow">
            <span className="mobileDetailsOutstandingRowLabel">Due + Undue Total</span>
            <span className="mobileDetailsOutstandingRowValue">{formatINR(outstandingAmt)}</span>
          </div>
        </div>

        {/* Sales & Receipts — month cards */}
        <div className="mobileDetailsSalesCard">
          <div className="mobileDetailsSalesHeader">
            <p className="mobileDetailsSalesTitle">Sales &amp; Receipts</p>
            <button className="mobileDetailsExportBtn" onClick={handleExportCSV}>
              Export
            </button>
          </div>

          {/* Horizontally scrollable area — prevents pills from squishing */}
          <div className="mobileDetailsSalesScroll">
            <div className="mobileDetailsSalesInner">

              {/* Column label row */}
              <div className="mobileDetailsSalesLabelRow">
                <span className="mobileDetailsSalesColLabel">QTY</span>
                <span className="mobileDetailsSalesColLabel">SALES</span>
                <span className="mobileDetailsSalesColLabel">RECEIPTS</span>
                <span className="mobileDetailsSalesColLabel">ORDER %</span>
              </div>

              {/* Month cards */}
              <div className="mobileDetailsSalesList">
                {monthlySummary.map((row, idx) => (
                  <div key={idx} className="mobileDetailsSalesRow">
                    <p className="mobileDetailsSalesMonth">
                      {MONTH_NAMES[row.Mn]?.slice(0, 3)} {row.Yr}
                    </p>
                    <div className="mobileDetailsSalesPills">
                      <div className="mobileDetailsSalesPill">
                        <span className="mobileDetailsSalesPillValue">
                          {row.TotalQty?.toLocaleString("en-IN") || "0"}
                        </span>
                      </div>
                      <div className="mobileDetailsSalesPill">
                        <span className="mobileDetailsSalesPillValue">
                          {formatINR(row.InvoiceAmt)}
                        </span>
                      </div>
                      <div className="mobileDetailsSalesPill">
                        <span className={`mobileDetailsSalesPillValue${row.ReceiptAmt > 0 ? " mobileDetailsSalesPillGreen" : " mobileDetailsSalesPillMuted"}`}>
                          {formatINR(row.ReceiptAmt)}
                        </span>
                      </div>
                      <div className="mobileDetailsSalesPill">
                        <span className={`mobileDetailsSalesPillValue${row["Order Share"] >= 50 ? " mobileDetailsSalesPillGreen" : " mobileDetailsSalesPillMuted"}`}>
                          {row["Order Share"] ? `${row["Order Share"].toFixed(2)}%` : "0%"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

      </div>

      {/* Invoice Modal */}
      {showInvoices && (
        <MobileInvoiceModal
          invoices={invoiceList}
          partyName={partyName}
          partyId={partyId}
          onClose={() => setShowInvoices(false)}
        />
      )}

      {/* Update Client Modal */}
      {showUpdateClient && (
        <MobileAddClient
          mode="update"
          partyId={Number(partyId)}
          onClose={() => setShowUpdateClient(false)}
        />
      )}
    </div>
  );
};